import {useEffect, useRef} from 'react'

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'wheel'] as const

export const DEFAULT_TIME_ON_PAGE_MS = 30_000
export const DEFAULT_IDLE_MS = 10_000

interface UseActivityTriggerArgs {
  enabled: boolean
  onTrigger: () => void
  timeOnPageMs?: number
  /** Idle window in ms; `null` disables the idle trigger (time-on-page only). */
  idleMs?: number | null
}

/**
 * Fires `onTrigger` once, whichever comes first: time on page reaches `timeOnPageMs`,
 * or the user is idle for `idleMs` (pass `null` to disable the idle trigger).
 * Qualifying interactions: pointerdown, keydown, scroll, wheel.
 */
export const useActivityTrigger = ({
  enabled,
  onTrigger,
  timeOnPageMs = DEFAULT_TIME_ON_PAGE_MS,
  idleMs = DEFAULT_IDLE_MS
}: UseActivityTriggerArgs) => {
  const onTriggerRef = useRef(onTrigger)
  onTriggerRef.current = onTrigger

  useEffect(() => {
    if (!enabled) return

    let hasTriggered = false

    const fire = (): void => {
      if (hasTriggered) return
      hasTriggered = true
      onTriggerRef.current()
    }

    const timeOnPageTimerId = setTimeout(fire, timeOnPageMs)

    const isIdleEnabled = idleMs != null
    let idleTimerId: ReturnType<typeof setTimeout> | undefined

    const onActivity = (): void => {
      clearTimeout(idleTimerId)
      idleTimerId = setTimeout(fire, idleMs as number)
    }

    if (isIdleEnabled) {
      idleTimerId = setTimeout(fire, idleMs)
      ACTIVITY_EVENTS.forEach(eventName => {
        document.addEventListener(eventName, onActivity, {passive: true})
      })
    }

    return () => {
      clearTimeout(idleTimerId)
      clearTimeout(timeOnPageTimerId)
      if (isIdleEnabled) {
        ACTIVITY_EVENTS.forEach(eventName => {
          document.removeEventListener(eventName, onActivity)
        })
      }
    }
  }, [enabled, idleMs, timeOnPageMs])
}
